import { Box, HStack } from "@chakra-ui/react";
import { colors, customEvent, EventPathType, useResponsive } from "goi_common";
import { useEffect, useRef, useState } from "react";

const DEFAULT_HEADER_HEIGHT = 56;
const DEFAULT_SECOND_HEADER_HEIGHT = 46;

interface NavigatorBarProps {
  sectionIds: string[];
  labels: string[];
  id?: string;
  eventPath: EventPathType;
  defaultOffset?: number;
  scrollSpyOffset?: number;
  secondHeaderHeight?: number;
}

/**
 * NavigatorBar - 섹션 네비게이션을 위한 스티키 탭 바 컴포넌트
 *
 * 스크롤에 따라 현재 섹션을 자동으로 감지하고,
 * 탭 클릭 시 해당 섹션으로 스무스 스크롤합니다.
 */
export const NavigatorBar = ({
  sectionIds,
  id = "navigator-bar",
  labels,
  eventPath,
  defaultOffset = DEFAULT_HEADER_HEIGHT,
  scrollSpyOffset = 180,
  secondHeaderHeight = DEFAULT_SECOND_HEADER_HEIGHT,
}: NavigatorBarProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const lastScrollYRef = useRef(
    typeof window === "undefined" ? 0 : window.scrollY
  );

  const [isSticky, setIsSticky] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);

  const [isSelectedSectionId, setIsSelectedSectionId] = useState<string | null>(
    sectionIds[0]
  );
  const { isBiggerThanLg } = useResponsive();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      const isDown = currentScrollY > lastScrollYRef.current;

      setIsScrollingDown(isDown);
      lastScrollYRef.current = currentScrollY;

      const visibleSection = sectionIds.reduce(
        (closest, id) => {
          const el = document.getElementById(id);
          if (!el) return closest;

          const top = el.getBoundingClientRect().top - scrollSpyOffset;
          if (top <= 0 && top > closest.offset) {
            return { id, offset: top };
          }
          return closest;
        },
        { id: sectionIds[0], offset: -Infinity }
      );

      setIsSelectedSectionId(visibleSection.id);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionIds, scrollSpyOffset]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: `-${defaultOffset}px 0px 0px 0px`,
        threshold: 0,
      }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => {
      observer.disconnect();
    };
  }, [defaultOffset]);

  const handleClick = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;

    const y =
      el.getBoundingClientRect().top +
      window.pageYOffset -
      scrollSpyOffset +
      (isBiggerThanLg ? 60 : 20);

    window.scrollTo({ top: y, behavior: "smooth" });

    customEvent({
      path: eventPath,
      trigger_and_target: `chip_${sectionId}` as never,
    });
    setIsSelectedSectionId(sectionId);
  };

  return (
    <>
      <div ref={sentinelRef} style={{ height: 1 }} />

      <HStack
        id={id}
        width="100%"
        padding="10px 24px"
        gap="8px"
        position={isSticky ? "sticky" : "relative"}
        top={
          isSticky
            ? `${
                isScrollingDown
                  ? isBiggerThanLg
                    ? defaultOffset - secondHeaderHeight
                    : defaultOffset - secondHeaderHeight - 40
                  : defaultOffset
              }px`
            : "auto"
        }
        zIndex={isSticky ? "500" : ""}
        backgroundColor={colors.white}
        transition="all 0.1s ease-in-out"
        marginTop="0px !important"
      >
        {labels.map((label: string, index: number) => {
          const currentId = sectionIds[index];
          return (
            <Box
              key={label}
              padding="5px 10px"
              borderRadius="18px"
              className={
                currentId === isSelectedSectionId
                  ? "subtitle_14_sb white"
                  : "body_14_m gray600"
              }
              backgroundColor={
                currentId === isSelectedSectionId
                  ? colors.gray900
                  : colors.gray50
              }
              onClick={() => handleClick(currentId)}
              cursor="pointer"
            >
              {label}
            </Box>
          );
        })}
      </HStack>
    </>
  );
};
